import type { MemberAmounts, ProposalResponse } from '@/types/waterfall'
import { MEMBERS, MEMBER_COLORS, MEMBER_LABELS, TIER_LABELS } from '@/types/waterfall'
import type { TierBreakdown } from '@/types/waterfall'
import { tierColumnTotal } from '@/utils/waterfallGrid'

const fmt = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
})

const TIER_ORDER: (keyof TierBreakdown)[] = ['t1', 'hp', 's3a', 's3b', 's4a', 's4b']

function StackedBar({ tier, total }: { tier: MemberAmounts; total: number }) {
  if (total <= 0) {
    return <div className="h-4 rounded bg-gray-100" />
  }
  const segments = MEMBERS.map((m) => ({
    m,
    amount: tier[m],
    width: (tier[m] / total) * 100,
  })).filter((s) => s.width > 0)

  return (
    <div className="flex h-4 overflow-hidden rounded ring-1 ring-white/80">
      {segments.map((seg, i) => (
        <div
          key={seg.m}
          style={{
            width: `${seg.width}%`,
            backgroundColor: MEMBER_COLORS[seg.m],
            boxShadow: i > 0 ? 'inset 1px 0 0 0 rgba(255,255,255,0.85)' : undefined,
          }}
          title={`${seg.m}: ${fmt.format(seg.amount)}`}
        />
      ))}
    </div>
  )
}

export default function WaterfallFlow({
  result,
  compact = false,
}: {
  result: ProposalResponse
  compact?: boolean
}) {
  const row = result.proposal.row_for_sheet
  const steps = result.steps
  let remaining = result.proposal.total

  return (
    <div className={compact ? 'space-y-1.5' : 'space-y-2'}>
      {!compact && (
        <div className="rounded-lg border border-brand-200 bg-brand-50/40 p-3">
          <p className="text-sm font-semibold text-gray-900">Distribution amount</p>
          <p className="text-2xl tabular-nums text-brand-800">{fmt.format(result.proposal.total)}</p>
        </div>
      )}

      {TIER_ORDER.map((tierKey, index) => {
        const tier = row[tierKey]
        const total = tierColumnTotal(tier)
        if (total === 0 && tierKey.startsWith('s4')) return null
        const step = steps[index]
        const remainingAfter =
          typeof step?.remaining_after === 'number' ? step.remaining_after : remaining - total
        remaining = remainingAfter

        return (
          <div key={tierKey} className="relative">
            {index > 0 && (
              <div className="flex justify-center py-0.5">
                <div
                  className="h-3 w-0.5 rounded-full bg-gray-300"
                  style={{ opacity: Math.max(0.25, remainingAfter / result.proposal.total) }}
                />
              </div>
            )}
            <div
              className={
                compact
                  ? 'rounded-md border border-gray-200 bg-white px-2.5 py-2 shadow-sm'
                  : 'rounded-lg border border-gray-200 bg-white p-3 shadow-sm'
              }
            >
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p className={compact ? 'text-sm font-medium text-gray-900' : 'font-medium text-gray-900'}>
                    {TIER_LABELS[tierKey]}
                  </p>
                  {!compact && (
                    <p className="text-xs text-gray-500 mt-0.5">
                      {String(step?.step ?? '').replace(/^\d+\.\s*/, '')}
                    </p>
                  )}
                </div>
                <p className="text-sm font-semibold tabular-nums">{fmt.format(total)}</p>
              </div>
              <div className={compact ? 'mt-1.5' : 'mt-2'}>
                <StackedBar tier={tier} total={total || 1} />
              </div>
              <div
                className={
                  compact
                    ? 'mt-1.5 flex flex-wrap gap-x-2 gap-y-0.5 text-[11px]'
                    : 'mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs'
                }
              >
                {MEMBERS.map((m) => {
                  if (compact && tier[m] <= 0) return null
                  return (
                  <span key={m} className={tier[m] > 0 ? 'text-gray-700' : 'text-gray-400'}>
                    <span
                      className="inline-block w-2 h-2 rounded-full mr-1 align-middle"
                      style={{ backgroundColor: MEMBER_COLORS[m], opacity: tier[m] > 0 ? 1 : 0.35 }}
                    />
                    {m}: {fmt.format(tier[m])}
                  </span>
                  )
                })}
              </div>
              {!compact && (
                <p className="mt-1.5 text-xs text-gray-500">
                  Remaining after tier: {fmt.format(remainingAfter)}
                </p>
              )}
            </div>
          </div>
        )
      })}

      {!compact && (
        <div className="rounded-lg border border-dashed border-gray-300 p-2 text-xs text-gray-500">
          {MEMBERS.map((m) => (
            <span key={m} className="mr-3">
              <strong>{m}</strong> {MEMBER_LABELS[m]}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
